// src/hooks/useStudentContext.js
//
// Pass 85.1A — CANONICAL STUDENT CONTEXT. Resolves a signed-in Auth uid to their coaching participant(s) and a SELECTED
// coaching organization, entirely from `participantProfiles` (authUserId == uid) — NOT from the legacy `users.orgId`.
//
//   • useParticipantSelf(uid): live participant profiles for this Auth account (distinct loading/error/empty states —
//     a read failure is NEVER rendered as "no profiles").
//   • useStudentContext(uid): the participant profiles + a selected org (persisted per-uid) + the resolved active
//     participant. If the Auth account has participants in >1 org it exposes an org choice; if it has >1 participant in
//     the SELECTED org it flags `ambiguous` and resolves NO active participant (we never silently pick one).

import { useEffect, useMemo, useState, useCallback } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { toStudentParticipant, buildStudentContextModel, resolveStudentAccessLane } from '../utils/participantSelfAccess';

// Live participant profiles for the signed-in Auth account. Scope-keyed to the uid so a sign-out/switch never flashes a
// prior account's profiles. Distinct { loading, error, profiles } — error is surfaced, never collapsed to empty.
export function useParticipantSelf(authUid) {
  const [snap, setSnap] = useState({ scopeKey: '', profiles: [], loading: true, error: null });
  const requestedScopeKey = authUid ? String(authUid) : '';
  useEffect(() => {
    setSnap({ scopeKey: requestedScopeKey, profiles: [], loading: !!requestedScopeKey, error: null });
    if (!requestedScopeKey) return undefined;
    const q = query(collection(db, 'participantProfiles'), where('authUserId', '==', requestedScopeKey));
    const unsub = onSnapshot(
      q,
      (s) => setSnap({ scopeKey: requestedScopeKey, profiles: s.docs.map((d) => toStudentParticipant(d.data(), d.id)), loading: false, error: null }),
      (err) => setSnap({ scopeKey: requestedScopeKey, profiles: [], loading: false, error: err || new Error('participant_self_read_failed') }),
    );
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [requestedScopeKey]);
  // Suppress a stale-scope snapshot synchronously at render.
  return useMemo(() => {
    if (snap.scopeKey !== requestedScopeKey) return { loading: !!requestedScopeKey, error: null, profiles: [] };
    return { loading: snap.loading, error: snap.error, profiles: snap.profiles };
  }, [snap, requestedScopeKey]);
}

const storageKey = (uid) => `motus.studentOrg.${uid}`;
const readStoredOrg = (uid) => { try { return uid ? (window.localStorage.getItem(storageKey(uid)) || '') : ''; } catch (_) { return ''; } };
const writeStoredOrg = (uid, org) => { try { if (uid) window.localStorage.setItem(storageKey(uid), org || ''); } catch (_) { /* private mode / blocked — selection is a convenience only */ } };

export function useStudentContext(authUid) {
  const { loading, error, profiles } = useParticipantSelf(authUid);
  const [requestedOrg, setRequestedOrg] = useState('');
  // Rehydrate the persisted org choice when the account changes (per-uid convenience; never authoritative privacy).
  useEffect(() => { setRequestedOrg(readStoredOrg(authUid)); }, [authUid]);

  const model = useMemo(() => buildStudentContextModel(profiles, requestedOrg), [profiles, requestedOrg]);

  const setSelectedOrganizationId = useCallback((org) => {
    setRequestedOrg(org || '');
    writeStoredOrg(authUid, org || '');
  }, [authUid]);

  const base = {
    loading,
    error,
    ...model, // orgOptions, hasAnyParticipant, needsOrgChoice, selectedOrganizationId, activeParticipant, ambiguous, ambiguousParticipants
    activeParticipantId: model.activeParticipant ? model.activeParticipant.participantId : '',
    setSelectedOrganizationId,
  };
  // Pass 85.1A.2 — the single canonical access lane every live student route branches on (legacy reads run ONLY when
  // lane === 'legacy'; every unresolved canonical state fails closed away from legacy).
  return { ...base, lane: resolveStudentAccessLane(base) };
}

export default useStudentContext;
